import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  lazy,
  Suspense,
} from "react";

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import {
  Container,
  Header,
  Content,
  Footer,
  Panel,
  Loader,
  Message,
  Tag,
  Timeline,
} from "rsuite";

import { ErrorBoundary } from "react-error-boundary";

import "rsuite/dist/rsuite.min.css";

import NavHeader from "./Header.js";
import NavFooter from "./Footer.js";
import { DraftGraphProvider } from "../../contexts/DraftGraphContext.tsx";


import {
  ToastContainer,
  toast,
  Slide,
  Zoom,
  Flip,
  Bounce,
} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
/* After Toastify so long-message wrap/scroll overrides win the cascade. */
import "./index.css";

import "../../auth/setupAxios.js";   // registers the axios interceptors once
import { fetchSession, logout } from "../../auth/session.js";

import {
  NAMED_KG_RINF,
  NAMED_KG_ERATV,
  NAMED_KG_ERA_VOCABULARY,
  NAMED_KG_ERA_SKOS,
  NAMED_KG_ERA_SHACL,
  SPARQL_ENDPOINT,
  API_BASE_URL,
  APP_ENV,
} from "../../config/config.js";

import WebworkerPromise from "webworker-promise";

const userInfoService = new WebworkerPromise(
  new Worker(new URL("./workers/UserInfo.worker.js", import.meta.url))
);

// Apps as components

const Landing = lazy(() => import("../Landing/Main.js"));

const Vocabulary = lazy(() => import("../Vocabulary/Main.js"));
const ApiDocs = lazy(() => import("../ApiDocs/Main.js"));
const DataStories = lazy(() => import("../DataStories/Main.js"));
const Endpoint = lazy(() => import("../Endpoint/Main.js"));

const EntityFormPage = lazy(() => import("../EntityForm/EntityFormPage.tsx"));
const UnifiedSearch = lazy(() => import("../UnifiedSearch/Main.tsx"));
const RouteCompatibility = lazy(() => import("../RouteCompatibility/Main.js"));
const DatasetExplorer = lazy(() => import("../DatasetExplorer/Main.js"));

const Describe = lazy(() => import("../Describe/Main.tsx"));
const NotificationsManager = lazy(() =>
  import("../NotificationsManager/Main.js")
);
const DatasetManager = lazy(() => import("../DatasetManager/Main.js"));


// Other context providers

export const AuthContext = createContext(null);
export const TranslationContext = createContext(null);
export const ThemeContext = createContext(null);
export const LocationContext = createContext(null);
export const ToolsContext = createContext(null);

// App root component

function DebugAzure() {
  const { auth, setRole, auth_debug } = useContext(AuthContext);

  return (
    <Container>
      <h3>Azure debug data</h3>
      <pre>{JSON.stringify(auth, null, "  ")}</pre>
      <pre>{JSON.stringify(auth_debug, null, "  ")}</pre>
    </Container>
  );
}

function Root() {
  //console.log("Initializing app");

  // Webpack populated
  const bundle = ENV;

  return (
      <ContextsWrapper />
    );
}

// Context wrapper

function ContextsWrapper() {
  // Auth / role

  const [auth, setAuth] = useState(undefined);
  const [authReady, setAuthReady] = useState(false);
  const [authInfo, setAuthInfo] = useState(null);
  const [authTokens, setAuthTokens] = useState(null);
  const [authRoles, setAuthRoles] = useState(null);
  const [authActiveRoles, setAuthActiveRoles] = useState({
    role: localStorage.getItem("activeRole"),
    company: localStorage.getItem("activeIM"),
    code: localStorage.getItem("activeCode"),
  });

  const [auth_debug, setAuthDebug] = useState();

  // Translation

  const [translation, setTranslation] = useState();

  // Theme

  const [theme, setTheme] = useState();

  // Tools

  const notify = (notification_type, notification_text) => {
    const method = {
      success: toast.success,
      info: toast.info,
      warn: toast.warn,
      error: toast.error,
    };

    if (method[notification_type] !== undefined) {
      method[notification_type](notification_text, {
        position: "top-center",
        autoClose: notification_type === "error" ? false : 10000,
        pauseOnFocusLoss: true,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Flip,
      });
    }
  };

  // Location

  const setRole = (role, company, code) => {
    if (role === "nre") {
      localStorage.setItem("activeIM", company);
      localStorage.setItem("activeCode", code);
    }

    if (role === "im") {
      localStorage.setItem("activeIM", "IM - Infrastructure Manager");
    }
    localStorage.setItem("activeCode", code);
    localStorage.setItem("activeRole", role);

    let active = {
      role: role || null,
      company: company || null,
      code: code || null,
    };

    setAuthActiveRoles(active);
  };

  useEffect(() => {
    setAuthRoles({
      set: setRole,
      active: authActiveRoles,
      available: authRoles?.available,
      company: authRoles?.company,
    });
  }, [authActiveRoles]);
  

  const fetchUserInfo = async () => {
    // No token to pass: the worker sends the HttpOnly session cookie itself.
    let response = await userInfoService.exec("getUserInfo", {});

    if (response?.srm?.roles && response?.srm?.info) {
      let role_check = [
        response?.srm?.roles?.nre,
        response?.srm?.roles?.im,
        response?.srm?.roles?.ru,
      ];

      if (role_check.some((x) => x)) {
        // Check if at least one role is true
        setAuthRoles({
          set: setRole,
          active: authActiveRoles,
          available: response?.srm?.roles,
          company: response?.srm?.company,
        });

        setAuthInfo({
          displayName: response?.srm?.info?.name,
          mail: response?.token?.info?.mail,
        });
      } else {
        // No data from auth backend
        notify(
          "error",
          "The account you logged does not have any assigned role, you'll be redirected to logout and use a different one."
        );

        setTimeout(() => {
          logout();
        }, 5 * 1000);

        setAuth(false);
      }
    } else {
      // No data from auth backend
      notify(
        "error",
        "The account you logged in is not registered on RINF, you'll be redirected to logout and use a different one."
      );

      setTimeout(() => {
        logout();
      }, 5 * 1000);

      setAuth(false);
    }
  };
  // Ask the backend once on page load. There is no token in the browser any
  // more; the HttpOnly session_id cookie is sent automatically.
  useEffect(() => {
    fetchSession().then((session) => {
      if (!session.authenticated || !session.user?.id) {
        setAuth(false);
        return;
      }
      // Different user than last time on this browser: reset the chosen role,
      // both in state and in localStorage (otherwise it comes back on reload).
      if (localStorage.getItem("activeAccount") !== session.user.id) {
        localStorage.setItem("activeAccount", session.user.id);
        localStorage.removeItem("activeRole");
        localStorage.removeItem("activeIM");
        localStorage.removeItem("activeCode");
        setAuthActiveRoles({ role: null, company: null, code: null });
      }
      // Placeholder only: callers still pass authTokens?.apiToken around,
      // but the value is never sent. Safe to remove with those call sites.
      setAuthTokens({ apiToken: {} });
      setAuth(true);
    });
  }, []);
  useEffect(() => {
    if (auth) {
      fetchUserInfo();
    }
  }, [auth]);
  // Once all effects are done, we trigger the ready state change
  useEffect(() => {
    if (auth === true) {
      if (authInfo !== null && authRoles != null) {
        //console.log(authRoles);
        setAuthReady(true);
      } else {
        setAuthReady(false);
      }
    }
    if (auth === false) {
      setAuthReady(true);
    }
  }, [auth, authInfo, authRoles]);

  const fallbackRender = ({ error, resetErrorBoundary }) => {
    return (
      <Message
        header={<b>Fatal error</b>}
        type="error"
        style={{ margin: "50px" }}
        showIcon
      >
        <p>A fatal error was found running the application.</p>
        <p>
          {" "}
          <b>{error.name}: </b>
          {error.message}
        </p>
        <Message type="info" style={{ marginTop: "25px" }}>
          <pre style={{ margin: "15px" }}>{error.stack}</pre>
        </Message>
      </Message>
    );
  };

  return (
    <AuthContext.Provider
      value={{ auth, authReady, authInfo, authTokens, authRoles }}
    >
      <TranslationContext.Provider value={{ translation, setTranslation }}>
        <ThemeContext.Provider value={{ theme, setTheme }}>
          <ToolsContext.Provider value={{ notify }}>
            <Router>
              <ErrorBoundary fallbackRender={fallbackRender}>
                <App />
              </ErrorBoundary>
            </Router>
          </ToolsContext.Provider>
        </ThemeContext.Provider>
      </TranslationContext.Provider>
    </AuthContext.Provider>
  );
}

// App main elements

function LazyRoute(props) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Route {...props} />
    </Suspense>
  );
}

function PageLoader() {
  return (
    <Panel
      style={{
        margin: "45vh 2rem 35vh 2rem",
      }}
    >
      <style>
        {`
          .loader {
            display: flex;
            justify-content: center;
            align-items: center;
            --color: hsl(220 100% 30%);
            --animation: 2s ease-in-out infinite;
          }

          .loader .circle {
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            width: 20px;
            height: 20px;
            border: solid 2px var(--color);
            border-radius: 50%;
            margin: 0 10px;
            background-color: transparent;
            animation: circle-keys var(--animation);
          }

          .loader .circle .dot {
            position: absolute;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background-color: var(--color);
            animation: dot-keys var(--animation);
          }

          .loader .circle .outline {
            position: absolute;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            animation: outline-keys var(--animation);
          }

          .circle:nth-child(2) {
            animation-delay: 0.3s;
          }

          .circle:nth-child(3) {
            animation-delay: 0.6s;
          }

          .circle:nth-child(4) {
            animation-delay: 0.9s;
          }

          .circle:nth-child(5) {
            animation-delay: 1.2s;
          }

          .circle:nth-child(2) .dot {
            animation-delay: 0.3s;
          }

          .circle:nth-child(3) .dot {
            animation-delay: 0.6s;
          }

          .circle:nth-child(4) .dot {
            animation-delay: 0.9s;
          }

          .circle:nth-child(5) .dot {
            animation-delay: 1.2s;
          }

          .circle:nth-child(1) .outline {
            animation-delay: 0.9s;
          }

          .circle:nth-child(2) .outline {
            animation-delay: 1.2s;
          }

          .circle:nth-child(3) .outline {
            animation-delay: 1.5s;
          }

          .circle:nth-child(4) .outline {
            animation-delay: 1.8s;
          }

          .circle:nth-child(5) .outline {
            animation-delay: 2.1s;
          }

          @keyframes circle-keys {
            0% {
              transform: scale(1);
              opacity: 1;
            }

            50% {
              transform: scale(1.5);
              opacity: 0.5;
            }

            100% {
              transform: scale(1);
              opacity: 1;
            }
          }

          @keyframes dot-keys {
            0% {
              transform: scale(1);
            }

            50% {
              transform: scale(0);
            }

            100% {
              transform: scale(1);
            }
          }

          @keyframes outline-keys {
            0% {
              transform: scale(0);
              outline: solid 20px var(--color);
              outline-offset: 0;
              opacity: 1;
            }

            100% {
              transform: scale(1);
              outline: solid 0 transparent;
              outline-offset: 30px;
              opacity: 0;
            }
          }
        `}
      </style>
      <div className="loader">
        <div className="circle">
          <div className="dot"></div>
          <div className="outline"></div>
        </div>
        <div className="circle">
          <div className="dot"></div>
          <div className="outline"></div>
        </div>
        <div className="circle">
          <div className="dot"></div>
          <div className="outline"></div>
        </div>
        <div className="circle">
          <div className="dot"></div>
          <div className="outline"></div>
        </div>
      </div>
    </Panel>
  );
}

function App() {
  const debugMode = localStorage.getItem("debugMode") === "1";
  const [ready, setReady] = useState(false);
  const [maintenance, setMaintenance] = useState();
  const { auth, authReady, authInfo, authTokens, authRoles } =
    useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  const [maintenanceInfo, setMaintenanceInfo] = useState({});

  // Prefix the browser tab title with the env on non-prod, so a user with
  // many tabs open can tell at a glance which environment a tab belongs to.
  useEffect(() => {
    if (APP_ENV && APP_ENV !== "PRO") {
      const base = "RINF - Register of Infrastructure";
      const prefixed = `[] ${base}`;
      if (document.title !== prefixed) document.title = prefixed;
    }
  }, []);

  const componentPaths = {
    "/": Landing,
    "/data-stories": DataStories,
    "/vocabulary": Vocabulary,
    "/era-api-docs": ApiDocs,
    "/endpoint": Endpoint,
    "/describe": Describe,
    "/unified-search": UnifiedSearch,
    "/route-compatibility": RouteCompatibility,
    "/dataset-explorer": DatasetExplorer,
    "/dataset-manager": DatasetManager,
    "/notifications-manager": NotificationsManager,
    "/create/:entityType": EntityFormPage,
    "/edit": EntityFormPage,
  };

  /*
  <Route exact path="/debug-azure" component={DebugAzure}></Route>
  <Route exact path="/test-component" component={TestComponent}></Route>
  */

  // There is a new endpoint that will return the next maintenance date and reason
  // For example {"current_time":"2024-09-04T11:10:46Z","next_maintenance_event":{"end_time":"2023-03-01T10:00:00Z","reason":"Server reboot","start_time":"2023-03-01T08:00:00Z"}}

  const apiEndpoint = API_BASE_URL + "/maintenance";

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await fetch(apiEndpoint);
      if (!response.ok) {
        throw new Error("Network error");
      }
      const data = await response.json();

      //console.log(data)

      return data;
    } catch (error) {
      console.error(error);
      return {};
    }
  };

  useEffect(() => {
   const legacySearchPaths = ["/map-explorer", "/search"];

   if (legacySearchPaths.includes(location.pathname)) {
    history.replace("/unified-search");
   }
  }, [history, location.pathname]);

  useEffect(() => {
    let intervalId;

    const checkMaintenanceStatus = async () => {
      const maintenanceData = await fetchMaintenanceStatus();

      if (
        maintenanceData &&
        maintenanceData.next_maintenance_event &&
        !debugMode
      ) {
        let lang = navigator.language || "de-DE";
        let formatter = new Intl.DateTimeFormat(lang, {
          dateStyle: "short",
          timeStyle: "long",
        });

        const startTime = new Date(
          maintenanceData.next_maintenance_event.start_time
        );
        const endTime = new Date(
          maintenanceData.next_maintenance_event.end_time
        );
        const currentTime = new Date(maintenanceData.current_time);

        setMaintenanceInfo({
          start_time: formatter.format(startTime),
          end_time: formatter.format(endTime),
          reason: maintenanceData.next_maintenance_event.reason,
        });

        if (currentTime >= startTime && currentTime <= endTime) {
          setMaintenance(true);
        } else {
          setMaintenance(false);
        }
      } else {
        setMaintenance(false);
      }

      intervalId = setTimeout(checkMaintenanceStatus, 5 * 60 * 1000); // 5 minute
    };

    checkMaintenanceStatus();

    return () => {
      clearTimeout(intervalId);
    };
  }, []);

  const prevMaintenance = useRef(maintenance);

  useEffect(() => {
    if (
      (maintenance === true && prevMaintenance.current === false) ||
      (maintenance === false && prevMaintenance.current === true)
    ) {
      window.location.reload();
    }
    prevMaintenance.current = maintenance;
  }, [maintenance]);

  useEffect(() => {
    if (authReady) {
      setReady(true);
    }
  }, [authReady]);

  return (
    <>
      <ToastContainer />
      {maintenance && (
        <Container>
          <Content style={{ height: "100vh" }}>
            <Panel style={{ margin: "calc(50vh - (300px/2)) 2rem" }}>
              <Message
                showIcon
                type="warning"
                header={<h5>Services are under maintenance</h5>}
              >
                <br />
                <p>
                  <b>Reason:</b> {maintenanceInfo.reason}
                </p>
                <br />
                <Timeline>
                  <Timeline.Item>
                    {maintenanceInfo.start_time}{" "}
                    <b>Maintenance planned start</b>
                  </Timeline.Item>
                  <Timeline.Item>
                    {maintenanceInfo.end_time} <b>Maintenance planned end</b>
                  </Timeline.Item>
                </Timeline>
                <br />
                <p>
                  <i>
                    This information is merely indicative, the time required may
                    be shorter or require extension.
                  </i>
                </p>
              </Message>
            </Panel>
          </Content>
        </Container>
      )}

      {ready && maintenance === false && (
        <Container>
          <Header>
            <NavHeader />
          </Header>
          <Content style={{ minHeight: "calc(100vh - 150px)" }}>
            <LocationContext.Provider value={{ location }}>
              <DraftGraphProvider>
                <Switch>
                  {Object.keys(componentPaths).map((path) => {
                    const Component = componentPaths[path];
                    return (
                      <LazyRoute
                        exact
                        key={path}
                        path={path}
                        component={Component}
                      />
                    );
                  })}
                  <Redirect to="/" />
                </Switch>
              </DraftGraphProvider>
            </LocationContext.Provider>
          </Content>
          <Footer>
            <NavFooter />
          </Footer>
        </Container>
      )}

      {!ready && !maintenance && (
        <Container>
          <Content style={{ height: "100vh" }}>
            <PageLoader />
          </Content>
        </Container>
      )}
    </>
  );
}

export default Root;
